import {
  ApolloCache,
  ApolloClient,
  NormalizedCacheObject,
} from '@apollo/client';
import { OpenCardsDocument } from './OpenCardsQuery.generated';
import { ClosedCardsDocument } from './ClosedCardsQuery.generated';
import { VisibleCardsDocument } from './VisibleCardsQuery.generated';
import { OpenListsDocument } from './OpenListsQuery.generated';
import { ClosedListsDocument } from './ClosedListsQuery.generated';
import { Maybe, Board, Card } from '../../generated';

export type GeneralizedCardsQuery = { __typename: 'Query' } & {
  board?: Maybe<
    { __typename: 'Board' } & Pick<Board, 'id'> & {
        cards: Array<{ __typename: 'Card' } & Pick<Card, 'id' | 'closed'>>;
      }
  >;
};

export type GeneralizedListsQuery = { __typename: 'Query' } & {
  board?: Maybe<
    { __typename: 'Board' } & Pick<Board, 'id'> & {
        lists: Array<{ __typename: 'List' } & Pick<Card, 'id' | 'closed'>>;
      }
  >;
};

export function removeDeleted(
  client: ApolloClient<unknown>,
  type: string,
  id: string,
) {
  client.cache.evict({ id: `${type}:${id}` });
  client.cache.gc();
}

type ListFilter = 'open' | 'closed';
type CardFilter = 'open' | 'closed' | 'visible';

export function assignCardToFilters({
  addTo,
  removeFrom,
  cache,
  boardId,
  cardId,
}: {
  addTo?: CardFilter[];
  removeFrom?: CardFilter[];
  cache: ApolloCache<NormalizedCacheObject>;
  boardId: string;
  cardId: string;
}) {
  if (addTo && addTo.length > 0) {
    addTo.forEach((filter) => {
      addCardToFilteredListOnBoard({
        filter,
        cache,
        boardId,
        cardId,
        closed: filter === 'closed',
      });
    });
  }
  if (removeFrom && removeFrom.length > 0) {
    removeFrom.forEach((filter) => {
      removeCardFromFilteredListOnBoard({
        filter,
        cache,
        boardId,
        cardId,
      });
    });
  }
}

export function assignListToFilters({
  addTo,
  removeFrom,
  cache,
  boardId,
  listId,
}: {
  addTo?: ListFilter[];
  removeFrom?: ListFilter[];
  cache: ApolloCache<NormalizedCacheObject>;
  boardId: string;
  listId: string;
}) {
  if (addTo && addTo.length > 0) {
    addTo.forEach((filter) => {
      addListToFilteredListOnBoard({
        filter,
        cache,
        boardId,
        listId,
        closed: filter === 'closed',
      });
    });
  }
  if (removeFrom && removeFrom.length > 0) {
    removeFrom.forEach((filter) => {
      removeListFromFilteredListOnBoard({
        filter,
        cache,
        boardId,
        listId,
      });
    });
  }
}

function getCardQueryForFilter(filter: CardFilter) {
  switch (filter) {
    case 'open':
      return OpenCardsDocument;
    case 'closed':
      return ClosedCardsDocument;
    case 'visible':
      return VisibleCardsDocument;
    default:
      throw new Error('Unsupported filter');
  }
}

function getListQueryForFilter(filter: ListFilter) {
  switch (filter) {
    case 'open':
      return OpenListsDocument;
    case 'closed':
      return ClosedListsDocument;
    default:
      throw new Error('Unsupported filter');
  }
}

function removeCardFromFilteredListOnBoard({
  filter,
  cache,
  boardId,
  cardId,
}: {
  filter: CardFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  boardId: string;
  cardId: string;
}) {
  const query = getCardQueryForFilter(filter);
  const existingCardsMatchingFilter = cache.readQuery<GeneralizedCardsQuery>({
    query,
    variables: {
      boardId,
    },
  });

  const newCardsMatchingFilter = existingCardsMatchingFilter?.board?.cards.filter(
    (card) => card.id !== cardId,
  );

  cache.writeQuery({
    query,
    data: {
      __typename: 'Query',
      board: {
        id: boardId,
        cards: [...(newCardsMatchingFilter ?? [])],
        __typename: 'Board',
      },
    },
    variables: {
      boardId,
    },
  });
}

function removeListFromFilteredListOnBoard({
  filter,
  cache,
  boardId,
  listId,
}: {
  filter: ListFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  boardId: string;
  listId: string;
}) {
  const query = getListQueryForFilter(filter);
  const existingListsMatchingFilter = cache.readQuery<GeneralizedListsQuery>({
    query,
    variables: {
      boardId,
    },
  });

  const newListsMatchingFilter = existingListsMatchingFilter?.board?.lists.filter(
    (list) => list.id !== listId,
  );

  cache.writeQuery({
    query,
    data: {
      __typename: 'Query',
      board: {
        id: boardId,
        lists: [...(newListsMatchingFilter ?? [])],
        __typename: 'Board',
      },
    },
    variables: {
      boardId,
    },
  });
}

function addCardToFilteredListOnBoard({
  filter,
  cache,
  boardId,
  cardId,
  closed,
}: {
  filter: CardFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  boardId: string;
  cardId: string;
  closed: boolean;
}) {
  const query = getCardQueryForFilter(filter);

  const existingCardsMatchingFilter = cache.readQuery<GeneralizedCardsQuery>({
    query,
    variables: {
      boardId,
    },
  });

  const newCardsMatchingFilter = existingCardsMatchingFilter?.board?.cards.filter(
    (card) => card.id !== cardId,
  );

  cache.writeQuery({
    query,
    data: {
      __typename: 'Query',
      board: {
        id: boardId,
        cards: [
          ...(newCardsMatchingFilter ?? []),
          {
            id: cardId,
            closed,
            __typename: 'Card',
          },
        ],
        __typename: 'Board',
      },
    },
    variables: {
      boardId,
    },
  });
}

function addListToFilteredListOnBoard({
  filter,
  cache,
  boardId,
  listId,
  closed,
}: {
  filter: ListFilter;
  cache: ApolloCache<NormalizedCacheObject>;
  boardId: string;
  listId: string;
  closed: boolean;
}) {
  const query = getListQueryForFilter(filter);

  const existingListsMatchingFilter = cache.readQuery<GeneralizedListsQuery>({
    query,
    variables: {
      boardId,
    },
  });

  const newListsMatchingFilter = existingListsMatchingFilter?.board?.lists.filter(
    (list) => list.id !== listId,
  );

  cache.writeQuery({
    query,
    data: {
      __typename: 'Query',
      board: {
        id: boardId,
        lists: [
          ...(newListsMatchingFilter ?? []),
          {
            id: listId,
            closed,
            __typename: 'List',
          },
        ],
        __typename: 'Board',
      },
    },
    variables: {
      boardId,
    },
  });
}

export function updateCardsInVisibleListOnBoard(
  cache: ApolloCache<NormalizedCacheObject>,
  boardId: string,
  cards: Pick<Card, 'id' | 'closed' | '__typename'>[],
  listClosed: boolean,
) {
  if (cards.length === 0) {
    // No changes required
    return;
  }
  const cardIds = cards.map((card) => card.id);
  const openCards = cards.filter((card) => !card.closed);
  const existingVisibleCards =
    cache.readQuery<GeneralizedCardsQuery>({
      query: VisibleCardsDocument,
      variables: {
        boardId,
      },
    })?.board?.cards || [];
  const otherVisibleCards = existingVisibleCards.filter(
    (card) => !cardIds.includes(card.id),
  );
  const newVisibleCards = listClosed
    ? otherVisibleCards
    : [...otherVisibleCards, ...openCards];

  // It's possible we could incorrectly hit this scenario. For example, if we
  // miss an "add" delta, but receive a "delete" delta, the lengths will be the
  // same, but the cache will not be updated correctly. This is self healing as
  // more deltas come in, and we believe it is a slim enough edge case that the
  // performance optimization is worthwhile.
  if (existingVisibleCards.length === newVisibleCards.length) {
    // we end up with the same list as already in cache
    // let's skip writing to cache
    return;
  }

  cache.writeQuery<GeneralizedCardsQuery>({
    query: VisibleCardsDocument,
    data: {
      __typename: 'Query',
      board: {
        id: boardId,
        cards: newVisibleCards,
        __typename: 'Board',
      },
    },
    variables: {
      boardId,
    },
  });
}
